import { useCallback, useEffect, useRef, useState } from "react";

export type ClipboardStatus = "idle" | "copied" | "failed";

interface UseClipboardOptions {
  /** How long to show the copied/failed status before resetting to idle (ms). Default: 2000 */
  resetDelay?: number;
}

interface UseClipboardReturn {
  /** Current status: 'idle', 'copied', or 'failed' */
  status: ClipboardStatus;
  /** Copy the given text to clipboard */
  copy: (text: string) => Promise<void>;
  /** Manually reset status to idle */
  reset: () => void;
}

/**
 * Hook for copying text to clipboard with status tracking.
 * Handles timer cleanup on unmount and provides consistent UX.
 */
export function useClipboard(
  options: UseClipboardOptions = {},
): UseClipboardReturn {
  const { resetDelay = 2000 } = options;
  const [status, setStatus] = useState<ClipboardStatus>("idle");
  const resetTimerRef = useRef<number | null>(null);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearResetTimer();
    setStatus("idle");
  }, [clearResetTimer]);

  const copy = useCallback(
    async (text: string) => {
      clearResetTimer();

      try {
        await navigator.clipboard.writeText(text);
        setStatus("copied");
      } catch {
        setStatus("failed");
      } finally {
        resetTimerRef.current = window.setTimeout(() => {
          setStatus("idle");
        }, resetDelay);
      }
    },
    [clearResetTimer, resetDelay],
  );

  useEffect(
    () => () => {
      clearResetTimer();
    },
    [clearResetTimer],
  );

  return { copy, reset, status };
}
